import { useState } from "react";
import { type LucideIcon, ChevronRight, Check } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
} from "@/shared/components/ui/sidebar";
import { Collapsible, CollapsibleContent } from "@/shared/components/ui/collapsible";
import { cn } from "@/shared/lib/utils";
import { useModuleProgress } from "@/shared/hooks/useModuleProgress";
import { getModuleUrl as getModuleReviewUrl } from "@/shared/constants/navSections";

export interface NavItem {
  title: string;
  url?: string;
  icon?: LucideIcon;
  isActive?: boolean;
  disabled?: boolean;
  comingSoon?: boolean;
  defaultOpen?: boolean;
  badge?: number;
  onClick?: () => void;
  subItems?: NavSubItem[];
}

export interface NavSubItem {
  title: string;
  url?: string;
  isActive?: boolean;
  disabled?: boolean;
  moduleId?: string;
  reviewScreen?: string;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

const CIRCLE_SIZE = 16;
const STROKE_WIDTH = 2.5;
const RADIUS = (CIRCLE_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function ModuleStatusIndicator({ progress }: { progress: number }) {
  if (progress >= 1) {
    return (
      <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand">
        <Check size={10} className="text-white" strokeWidth={3} />
      </div>
    );
  }
  if (progress > 0) {
    const offset = CIRCUMFERENCE * (1 - progress);
    return (
      <svg width={CIRCLE_SIZE} height={CIRCLE_SIZE} className="shrink-0 -rotate-90">
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="#d6d3d1"
          strokeWidth={STROKE_WIDTH}
        />
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="#008251"
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return <div className="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-stone-300" />;
}

const NAV_EXPAND_KEY = "nav-v3-expanded";

function getExpandedState(): Record<string, boolean> {
  const stored = sessionStorage.getItem(NAV_EXPAND_KEY);
  return stored ? (JSON.parse(stored) as Record<string, boolean>) : {};
}

function NavItemWithSub({ item }: { item: NavItem }) {
  const [isOpen, setIsOpen] = useState(
    () => getExpandedState()[item.title] ?? item.defaultOpen ?? false,
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { modules } = useModuleProgress();
  const hasModuleSubItems = item.subItems?.some((s) => s.moduleId);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    const state = getExpandedState();
    state[item.title] = open;
    sessionStorage.setItem(NAV_EXPAND_KEY, JSON.stringify(state));
  };

  const isCurrentRoute = !!item.url && location.pathname === item.url;

  return (
    <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip={item.title}
          onClick={() => {
            if (item.url) {
              navigate(item.url);
              if (!isOpen) handleOpenChange(true);
            } else {
              handleOpenChange(!isOpen);
            }
          }}
          className={cn(
            "text-primary hover:bg-background [&>svg]:size-4",
            isCurrentRoute && "bg-background text-primary font-medium",
          )}
        >
          {item.icon && <item.icon size={16} />}
          <span>{item.title}</span>
          <ChevronRight
            size={16}
            className={cn("ml-auto transition-transform duration-200", isOpen && "rotate-90")}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenChange(!isOpen);
            }}
          />
        </SidebarMenuButton>
        <CollapsibleContent>
          <SidebarMenuSub className="ml-[24px] mr-0 px-0 py-0 gap-0 border-l border-primary">
            {item.subItems!.map((subItem) => {
              const subItemPath = subItem.url?.split("?")[0];
              const isSubCurrentRoute = !!subItemPath && location.pathname.includes(subItemPath);
              const moduleEntry =
                hasModuleSubItems && subItem.moduleId
                  ? modules.find((m) => m.id === subItem.moduleId)
                  : null;

              const getModuleUrl = () => {
                if (!subItem.url || !subItem.moduleId) return subItem.url;
                if (!moduleEntry) return subItem.url;
                return getModuleReviewUrl(subItem.moduleId);
              };

              return (
                <SidebarMenuSubItem key={subItem.title}>
                  <SidebarMenuButton
                    tooltip={subItem.title}
                    onClick={
                      subItem.disabled || !subItem.url ? undefined : () => navigate(getModuleUrl()!)
                    }
                    disabled={subItem.disabled}
                    className={cn(
                      "text-primary hover:bg-background [&>svg]:size-4",
                      subItem.disabled && "opacity-50 cursor-not-allowed",
                      isSubCurrentRoute && "bg-background text-primary font-medium",
                    )}
                  >
                    <span className="flex-1">{subItem.title}</span>
                    {moduleEntry && <ModuleStatusIndicator progress={moduleEntry.progress} />}
                  </SidebarMenuButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

export function NavMain({ sections }: { sections: NavSection[] }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <>
      {sections.map((section, sectionIndex) => (
        <SidebarGroup key={section.label || sectionIndex} className="px-2 py-0">
          {section.label && (
            <SidebarGroupLabel className="h-auto px-2 pb-2 pt-4 text-xs text-tertiary">
              {section.label}
            </SidebarGroupLabel>
          )}
          <SidebarMenu className="gap-2">
            {section.items.map((item) => {
              if (item.subItems && item.subItems.length > 0) {
                return <NavItemWithSub key={item.title} item={item} />;
              }

              const isCurrentRoute =
                !item.comingSoon && !!item.url && location.pathname.includes(item.url);

              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    tooltip={item.title}
                    onClick={
                      item.comingSoon || item.disabled
                        ? undefined
                        : item.onClick
                          ? item.onClick
                          : item.url
                            ? () => navigate(item.url!)
                            : undefined
                    }
                    disabled={item.disabled}
                    aria-disabled={item.comingSoon || undefined}
                    className={cn(
                      "text-primary hover:bg-background [&>svg]:size-4",
                      (item.disabled || item.comingSoon) && "opacity-50 cursor-not-allowed",
                      isCurrentRoute && "bg-background text-primary font-medium",
                    )}
                  >
                    {item.icon && <item.icon size={16} />}
                    <span>{item.title}</span>
                    {!!item.badge && (
                      <span className="ml-auto mr-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand px-1 text-[11px] leading-none font-medium text-white">
                        {item.badge}
                      </span>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  );
}
